import { Colors, Icon, Spinner } from '@amelio/ui-library';
import { useId } from 'react';

import type { EmployeeEvolution } from '@/api/performance-profile/careerGrowthService';
import type { IconName } from '@amelio/ui-library';
import type { ReactNode } from 'react';

import { GROWTH_GLASS_SURFACE } from '@/components/CareerGrowth/careerGrowthStyles';
import { useAmelioTranslations } from '@/hooks';

interface SummarySectionProps {
    icon: IconName;
    title: string;
    subtitle?: string;
    /** Drawn across from the title, such as a switch between views of the section. */
    aside?: ReactNode;
    /** The evolution journey both sections of the summary read. */
    evolution: { data?: EmployeeEvolution; isLoading: boolean; isError: boolean };
    /** The section's content, once the evolution journey is known. */
    children: (evolution: EmployeeEvolution) => ReactNode;
    dataTestId: string;
}

/**
 * A glass section of the portal's summary: its header, then its content, or the loading or the
 * failure of the evolution journey it reads -- never an empty state before the answer is known.
 */
export const SummarySection = ({
    icon,
    title,
    subtitle,
    aside,
    evolution,
    children,
    dataTestId,
}: SummarySectionProps) => {
    const titleId = useId();

    return (
        <section
            aria-labelledby={titleId}
            className={`${GROWTH_GLASS_SURFACE} flex flex-col gap-6 p-6 sm:p-10`}
            data-testid={dataTestId}
        >
            <header className={'flex flex-col gap-4 md:flex-row md:items-start md:justify-between'}>
                <div className={'flex items-center gap-3'}>
                    <Icon
                        name={icon}
                        size={30}
                        colorClassName={'text-navy'}
                        className={'shrink-0'}
                        ariaHidden
                    />
                    <div>
                        <h2
                            id={titleId}
                            className={'text-navy text-[20px] leading-tight font-medium'}
                        >
                            {title}
                        </h2>
                        {subtitle && (
                            <p className={'text-growth-slate text-[14px] font-medium'}>
                                {subtitle}
                            </p>
                        )}
                    </div>
                </div>
                {aside}
            </header>

            <SectionBody
                evolution={evolution}
                dataTestId={dataTestId}
            >
                {children}
            </SectionBody>
        </section>
    );
};

const SectionBody = ({
    evolution: { data, isLoading, isError },
    children,
    dataTestId,
}: Pick<SummarySectionProps, 'evolution' | 'children' | 'dataTestId'>) => {
    const { t } = useAmelioTranslations();

    if (isLoading) {
        return (
            <div
                className={'flex justify-center p-8'}
                data-testid={`${dataTestId}-loading`}
            >
                <Spinner
                    size={'md'}
                    color={Colors.PRIMARY}
                />
            </div>
        );
    }

    if (isError || !data) {
        return (
            <p
                role={'alert'}
                className={'text-error text-[14px]'}
            >
                {t('employeePortalSummary.loadError')}
            </p>
        );
    }

    return children(data);
};
